import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BaseTimeslotBodyInterface } from '@sport-activities/sdk';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { Timeslot } from '~/core/model/timeslot.model';
import { repeatUntil } from '~/core/operators/repeat-until.operator';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';

@Injectable({
  providedIn: 'root',
})
export class TimeslotUpdateService {
  public partialTimeslot = new BehaviorSubject<Partial<Timeslot>>(null);
  public partialTimeslotBody = new BehaviorSubject<
    Partial<BaseTimeslotBodyInterface>
  >(null);

  constructor(
    private readonly activityService: OrganizerSharedActivityService
  ) {}

  public getTimeslotsToUpdate(
    selectedTimeslots: FormControl,
    timeslots: Timeslot[]
  ): Timeslot[] {
    if (selectedTimeslots.value['select-all']) {
      return timeslots;
    }

    return timeslots.filter(
      (timeslot) => selectedTimeslots.value.timeslots[timeslot.identifier]
    );
  }

  public setPartialTimeslotBody(
    data: Partial<BaseTimeslotBodyInterface>
  ): void {
    this.partialTimeslotBody.next(data);
  }

  public setPartialTimeslotUpdates(data: Partial<Timeslot>): void {
    this.partialTimeslot.next(data);
  }

  public updateTimeslots(
    activityId: number | string,
    timeslots: Timeslot[],
    validationFunction: (a: Timeslot, b: Partial<Timeslot>) => boolean
  ): Observable<[string[], Timeslot]> {
    return combineLatest([
      this.partialTimeslotBody.pipe(
        mergeMap((partialTimeslotBody) =>
          this.activityService.patchTimeslotBulk(
            activityId.toString(),
            timeslots.map((timeslot) => ({
              ...partialTimeslotBody,
              identifier: timeslot.identifier,
            }))
          )
        )
      ),
      this.partialTimeslot.pipe(
        mergeMap((partialTimeslot) => {
          return this.activityService
            .getTimeslot(timeslots?.[0]?.identifier)
            .pipe(
              repeatUntil((timeslot) => {
                return validationFunction(timeslot, partialTimeslot);
              })
            );
        })
      ),
    ]);
  }
}
